<script setup lang="ts">
import { computed } from 'vue'
import { bbox, centroid, distance, length } from '@turf/turf'
import { calculateArea, countVertices, formatAreaDetailed } from '@/utils/geometry'
import { hasRealGeometry } from '@/utils/mapbox/staticMap'
import { formatNumber } from './format'

const props = defineProps<{
  geometry: Record<string, any> | null | undefined
  planetAreaKm2?: number | null
}>()

const valid = computed(() => !!props.geometry && hasRealGeometry({ geometry: props.geometry }))

const stats = computed(() => {
  if (!valid.value) return null
  const geometry = props.geometry as any
  const feature = { type: 'Feature', properties: {}, geometry } as any
  const areaM2 = calculateArea(geometry)
  const areaKm2 = areaM2 / 1_000_000
  const [minX, minY, maxX, maxY] = bbox(feature)
  const widthKm = distance([minX, minY], [maxX, minY], { units: 'kilometers' })
  const heightKm = distance([minX, minY], [minX, maxY], { units: 'kilometers' })
  const c = centroid(feature).geometry.coordinates
  let lengthKm: number | null = null
  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    lengthKm = length(feature, { units: 'kilometers' })
  }
  return {
    type: geometry.type as string,
    areaM2,
    areaKm2,
    vertices: countVertices(geometry),
    widthKm,
    heightKm,
    centroid: c,
    lengthKm,
  }
})

const billableDiffers = computed(() => {
  if (props.planetAreaKm2 == null || !stats.value) return false
  return Math.abs(props.planetAreaKm2 - stats.value.areaKm2) > Math.max(0.5, stats.value.areaKm2 * 0.02)
})
</script>

<template>
  <div class="text-xs space-y-0.5">
    <div class="text-muted-foreground font-medium">Geometry</div>
    <template v-if="stats">
      <div><span class="text-muted-foreground">Type:</span> {{ stats.type }}</div>
      <div v-if="stats.areaM2 > 0"><span class="text-muted-foreground">Size:</span> {{ formatAreaDetailed(stats.areaM2) }}</div>
      <div v-else-if="stats.type === 'Point'" class="text-muted-foreground italic">Point — Planet buffers to a 5×5 km square (25 km²)</div>
      <div v-if="stats.lengthKm != null"><span class="text-muted-foreground">Length:</span> {{ formatNumber(stats.lengthKm, 2) }} km</div>
      <div v-if="planetAreaKm2 != null" :class="billableDiffers ? 'text-amber-600 dark:text-amber-400' : ''">
        <span class="text-muted-foreground">Planet billable area:</span> {{ formatNumber(planetAreaKm2, 2) }} km²
      </div>
      <div><span class="text-muted-foreground">Vertices:</span> {{ stats.vertices.toLocaleString() }}</div>
      <div v-if="stats.type !== 'Point'"><span class="text-muted-foreground">Extent:</span> {{ formatNumber(stats.widthKm, 1) }} × {{ formatNumber(stats.heightKm, 1) }} km</div>
      <div><span class="text-muted-foreground">Center:</span> {{ stats.centroid[1].toFixed(4) }}, {{ stats.centroid[0].toFixed(4) }}</div>
    </template>
    <div v-else class="text-muted-foreground italic">No geometry</div>
  </div>
</template>
